import React from 'react';
import type { BoxProps } from '@withjoy/joykit';
import { Box, TextV2 } from '@withjoy/joykit';

import type { CustomerProductReviewFragment, CustomerReviewStatsFragment } from '@graphql/generated';
import { SkeletonGroup, SkeletonText } from '@shared/components/Skeleton';
import { StatsSummary } from './components/StatsSummary';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { ReviewList } from './components/ReviewList';
import type { UseReviewOrderingReturn } from './useReviewSorting';
import { useTranslation } from '@shared/core';
import { useCustomerReviews } from './useCustomerReviews';
import { rootStyles } from './CustomerReviews.styles';
import { useShopStorefrontContext } from '../../../ShopStorefrontProvider';

interface Props extends UseReviewOrderingReturn {
  stats: Maybe<CustomerReviewStatsFragment>;
  reviews: CustomerProductReviewFragment[];
  hasMoreReviews: boolean;
  onShowMoreReviewsClick?: () => void;
}

const Root = (props: Props) => {
  const { reviews, stats, hasMoreReviews, onShowMoreReviewsClick, orderingOptions, currentOrderingOption, handleOnSetReviewOrdering } = props;

  const { t } = useTranslation('catalogRegistry');
  const { currentStorefront } = useShopStorefrontContext();

  const sectionTitleByStorefront = t('catalogItemCustomerReviewsSection')[currentStorefront]?.sectionTitle?.();
  return (
    <Box __css={rootStyles}>
      <TextV2
        component="h3"
        typographyVariant={['HedMobile32', null, 'Hed56']}
      >
        {sectionTitleByStorefront}
      </TextV2>

      {stats && <StatsSummary stats={stats} />}

      <ReviewList
        reviews={reviews}
        hasMoreReviews={hasMoreReviews}
        onShowMoreReviewsClick={onShowMoreReviewsClick}
        orderingOptions={orderingOptions}
        currentOrderingOption={currentOrderingOption}
        handleOnSetReviewOrdering={handleOnSetReviewOrdering}
      />
    </Box>
  );
};

type CustomerReviewsProps = {
  catalogItemId: string;
  showSkeleton?: boolean;
} & BoxProps;

const CustomerReviewsContent = React.forwardRef<HTMLElement, CustomerReviewsProps & { isOnScreen: boolean }>(
  ({ catalogItemId, id, isOnScreen, showSkeleton, ...restProps }, ref) => {
    const { stats, reviews, loading, orderingOptions, currentOrderingOption, handleOnSetReviewOrdering, hasMoreReviews, handleOnShowMoreReviewsClick } = useCustomerReviews({
      catalogItemId,
      isOnScreen,
    });
    return (
      <Box
        as="section"
        ref={ref}
      >
        {!loading && !reviews.length && isOnScreen ? null : (
          <SkeletonGroup
            isReady={!!reviews.length && !showSkeleton}
            placeholder={
              <Box
                __css={{
                  backgroundColor: 'mono2',
                  paddingY: [6, null, 10],
                  paddingX: [6, null, 88],

                  marginX: [-6, null, 0],
                  borderRadius: 'inherit',
                }}
              >
                <SkeletonText
                  rows={1}
                  skeletonHeight={67}
                  width={['100%', 600]}
                />
                <StatsSummary.Skeleton />
                <ReviewList.Skeleton />
              </Box>
            }
            id={id}
            {...restProps}
          >
            <Root
              stats={stats}
              reviews={reviews}
              orderingOptions={orderingOptions}
              currentOrderingOption={currentOrderingOption}
              handleOnSetReviewOrdering={handleOnSetReviewOrdering}
              hasMoreReviews={hasMoreReviews}
              onShowMoreReviewsClick={handleOnShowMoreReviewsClick}
            />
          </SkeletonGroup>
        )}
      </Box>
    );
  },
);

const LazyLoadedContent = withLazyPropOnIntersection(CustomerReviewsContent, 'isOnScreen');

const CustomerReviews = (props: CustomerReviewsProps) => {
  return (
    <LazyLoadedContent
      {...props}
      isOnScreen={{ onscreen: true, offscreen: false }}
    />
  );
};

export { CustomerReviews };
